/** "What have we achieved, and what will reaching the goal take?"
 *
 *  Requested 2026-09-27 for the campaign overview. Kept as a pure
 *  module because every number here is a CLAIM about the campaign —
 *  claims belong under test, not inside a component.
 *
 *  Two decisions worth knowing before changing anything:
 *
 *  WORKING DAYS, NOT CALENDAR DAYS. On the campaign that prompted this,
 *  inspections closed on 18, 21, 22, 23, 24, 25 and 27 September and on
 *  no weekend. Dividing by calendar days understated the required rate
 *  by about a third and flattered the achieved one. If a campaign really
 *  does run weekends the series will show it, and `achievedPerDay` reads
 *  from the same denominator, so the comparison stays fair either way.
 *
 *  A PROJECTION IS NOT A FORECAST. A straight line off nine days will
 *  scream on any campaign that is still ramping, so `tooEarly` gates the
 *  warning rather than leaving the caller to remember.
 */

export type ClosedDay = { date: string; closed: number };

export type GoalInput = {
  /** Beneficiaries in the campaign — the goal is to inspect all of them. */
  total: number;
  /** Beneficiaries already inspected. */
  made: number;
  closedByDay: ClosedDay[];
  startDate: string | null;
  endDate: string | null;
  /** Today, injected so this stays pure and testable. */
  today: Date;
  /** Slots the roster can serve per day, for the "is it capacity?" call. */
  dailyCapacity?: number;
};

/** What the verdict banner says. Five states, not two — a page whose
 *  banner is only ever red is a page people learn to ignore, and one
 *  that goes blank when things are fine reads as a failed load.
 *
 *    complete     every beneficiary inspected — say so and stop counting
 *    no_deadline  no end date, so there is no pace to be off
 *    too_early    under MIN_DAYS_TO_PROJECT of history; a straight line
 *                 here describes the ramp, not the campaign
 *    on_pace      the current rate finishes the work in time
 *    off_pace     it does not
 */
export type GoalStatus =
  | "complete"
  | "no_deadline"
  | "too_early"
  | "on_pace"
  | "off_pace";

export type Goal = {
  status: GoalStatus;
  remaining: number;
  /** Working days between today and the end date, today excluded. */
  workingDaysLeft: number;
  /** Working days on which the campaign has actually run. */
  workingDaysElapsed: number;
  /** Whole inspections. Nobody performs 4.3 of one, and a rate printed
   *  as "4.3 a day" reads as a quantity that does not exist. Both are
   *  rounded for display; `factor` keeps the true ratio. */
  achievedPerDay: number;
  neededPerDay: number;
  /** Multiple of the current rate required to finish on time. */
  factor: number;
  onPace: boolean;
  /** True while there is too little history to project honestly. */
  tooEarly: boolean;
  /** Capacity is NOT the constraint when the needed rate fits inside it. */
  capacityIsEnough: boolean | null;
  /** Working days the remaining work would take at the current rate. */
  daysAtCurrentRate: number | null;
};

/** Below this, a straight-line projection says more about the ramp than
 *  about the campaign. Five working days is one week of operating. */
export const MIN_DAYS_TO_PROJECT = 5;

function isWeekend(d: Date): boolean {
  const day = d.getUTCDay();
  return day === 0 || day === 6;
}

/** Working days strictly after `from`, up to and including `to`. */
export function workingDaysBetween(from: Date, to: Date): number {
  let n = 0;
  const cur = new Date(
    Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()),
  );
  const end = new Date(
    Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate()),
  );
  while (cur < end) {
    cur.setUTCDate(cur.getUTCDate() + 1);
    if (!isWeekend(cur)) n += 1;
  }
  return n;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** The rate the deadline demands, as whole inspections.
 *
 *  Rounded UP on purpose: 40.6 a day means 40 is not enough. Rounding
 *  to nearest would print a target you could hit and still miss the
 *  date. */
function neededWhole(n: number): number {
  return Number.isFinite(n) ? Math.ceil(n) : n;
}

/** The rate actually being achieved, as whole inspections.
 *
 *  Nearest, not up: this is a description of what happened, and
 *  flattering it would undercut the very gap the card exists to show. */
function achievedWhole(n: number): number {
  return Math.round(n);
}

export function computeGoal(input: GoalInput): Goal {
  const { total, made, closedByDay, endDate, today, dailyCapacity } = input;
  const remaining = Math.max(0, total - made);

  // Days the campaign actually produced something. Using days-with-output
  // rather than days-since-launch avoids punishing a campaign for the
  // stretch before its first booking landed.
  const activeDays = closedByDay.filter((d) => d.closed > 0).length;
  const workingDaysElapsed = activeDays;
  const closed = closedByDay.reduce((s, d) => s + d.closed, 0);
  const achievedPerDay = workingDaysElapsed > 0 ? closed / workingDaysElapsed : 0;

  const end = endDate ? new Date(`${endDate}T00:00:00Z`) : null;
  const workingDaysLeft = end ? workingDaysBetween(today, end) : 0;
  const neededPerDay =
    workingDaysLeft > 0 ? remaining / workingDaysLeft : remaining > 0 ? Infinity : 0;

  const tooEarly = workingDaysElapsed < MIN_DAYS_TO_PROJECT;
  const factor =
    achievedPerDay > 0 && Number.isFinite(neededPerDay)
      ? neededPerDay / achievedPerDay
      : Infinity;

  const status: GoalStatus =
    remaining === 0
      ? "complete"
      : end === null
        ? "no_deadline"
        : tooEarly
          ? "too_early"
          : achievedPerDay >= neededPerDay
            ? "on_pace"
            : "off_pace";

  return {
    status,
    remaining,
    workingDaysLeft,
    workingDaysElapsed,
    achievedPerDay: achievedWhole(achievedPerDay),
    neededPerDay: neededWhole(neededPerDay),
    // Kept to one decimal, and computed from the UNROUNDED rates: a
    // multiple is not a count of inspections, and deriving it from the
    // rounded pair would compound two roundings into a visibly wrong
    // ratio on small numbers.
    factor: Number.isFinite(factor) ? round1(factor) : Infinity,
    // No end date means no deadline to miss, so nothing to warn about.
    onPace: end === null ? true : achievedPerDay >= neededPerDay,
    tooEarly,
    capacityIsEnough:
      dailyCapacity === undefined || !Number.isFinite(neededPerDay)
        ? null
        : neededPerDay <= dailyCapacity,
    daysAtCurrentRate:
      achievedPerDay > 0 ? Math.ceil(remaining / achievedPerDay) : null,
  };
}
